import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DismissMethod,
  mountSupportWidget,
  SupportWidgetConfig,
  SupportWidgetHandle
} from 'Design-system/app/static/scripts/support-widget';
import { SupportChannel } from 'Douleutaras/app/lib/posthog/posthog.types';
import agentImage from 'Design-system/app/static/images/support-widget/agent.png';

import './support-widget.global.scss';

export interface SupportSheetProps {
  /** Opening and closing is the caller's decision; this component only reflects it. */
  isOpen: boolean;
  chatEnabled: boolean;
  callEnabled: boolean;
  phone: string;
  /** Pre-composed sentence; when empty, the hour line is dropped entirely. */
  hoursLabel: string;
  /** True, while the chat bundle is still resolving — the button shows busy, never fails. */
  chatBusy?: boolean;
  onClose(method: DismissMethod): void;
  onOpenChat(): void;
  onChannelSelected(channel: SupportChannel): void;
}

/**
 * The customer-support sheet, as an embedded (launcherless) surface.
 *
 * Markup mirrors `design-system/app/templates/partials/support-widget.hbs` and
 * behavior is the design-system core — this component supplies neither. What it
 * supplies is the DOM the core binds to via its `data-support-*` hooks, and the
 * injected `openChat`, which is what keeps the core free of any chat SDK.
 *
 * Only the **online** views are rendered here. The contact-form channel is the
 * out-of-hours channel, and every surface that uses this component hides itself
 * out of hours, so there is no state for it to appear in (#5622 AC4). An absent
 * channel is absent, never disabled.
 */
export const SupportSheet = ({
  isOpen,
  chatEnabled,
  callEnabled,
  phone,
  hoursLabel,
  chatBusy = false,
  onClose,
  onOpenChat,
  onChannelSelected
}: SupportSheetProps) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLElement>(undefined);
  const handleRef = useRef<SupportWidgetHandle>(undefined);
  // Read inside the core's `track` bridge, which outlives each render.
  const callbacks = useRef({ onClose, onOpenChat, onChannelSelected });

  callbacks.current = { onClose, onOpenChat, onChannelSelected };

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    // The core only reads `state`, `delayMs` and the presence of `channels`;
    // everything visible is rendered above. `reveal: 'external'` means it never
    // starts a timer — this surface is opened by a trigger, not by waiting.
    const config: SupportWidgetConfig = {
      state: 'available',
      delayMs: 0,
      hoursLabel,
      channels: {
        ...(chatEnabled ? { chat: { label: t('T.SUPPORT-WIDGET.CHANNEL.CHAT', 'Chat') } } : {}),
        ...(callEnabled ? { phone: { label: t('T.SUPPORT-WIDGET.CHANNEL.PHONE', 'Phone'), href: `tel:${phone}` } } : {})
      },
      agent: { image: agentImage, alt: '' },
      copy: {
        title: t('T.SUPPORT-WIDGET.SHEET.TITLE', 'Need help?'),
        subtitleOnline: t('T.SUPPORT-WIDGET.SHEET.SUBTITLE', 'Our team is here for whatever you need.'),
        subtitleOffline: ''
      }
    };

    handleRef.current = mountSupportWidget({
      root: rootRef.current,
      config,
      reveal: 'external',
      openChat: () => callbacks.current.onOpenChat(),
      track: (event, payload) => {
        switch (event) {
          case 'support_widget_chat':
            callbacks.current.onChannelSelected(SupportChannel.CHAT);
            break;
          case 'support_widget_phone':
            callbacks.current.onChannelSelected(SupportChannel.CALL);
            break;
          case 'support_widget_closed':
            // Esc, the backdrop and the × all land here, so the owner of `isOpen`
            // hears about every close and not just the one it initiated.
            callbacks.current.onClose((payload as { method: DismissMethod })?.method);
            break;
          default:
            // `support_widget_opened` is deliberately unmapped: an embedded
            // surface's impression is its own Shown event, so it must not also
            // fire `Support Widget Clicked` (tracking plan §1).
        }
      }
    });

    return () => handleRef.current?.destroy();
    // Re-mounting on copy changes would tear down an open sheet; the core reads
    // `config` once, and the rendered text above is already reactive.
  }, []);

  useEffect(() => {
    if (isOpen) {
      handleRef.current?.openSheet();
    } else {
      handleRef.current?.closeSheet();
    }
  }, [isOpen]);

  return (
    <aside
      /* className, not styleName — see the comment at the top of support-widget.global.scss */
      className="support-widget support-widget--launcherless support-widget--online"
      ref={rootRef}
      data-support-widget
      aria-label={t('T.SUPPORT-WIDGET.LABEL', 'Customer support')}
    >
      { /* `aria-modal` only while open, and only because the core traps Tab inside the
           sheet — claiming it without the trap would tell AT to ignore a page the
           keyboard can still reach. Closed, the sheet is `visibility: hidden`, so it
           leaves the accessibility tree and the tab order on its own. */ }
      <div className="support-widget-sheet" data-support-sheet role="dialog"
           aria-modal={isOpen}
           aria-label={t('T.SUPPORT-WIDGET.LABEL', 'Customer support')}>

        <button type="button" className="support-widget-sheet__back" data-support-back
                aria-label={t('T.SUPPORT-WIDGET.BACK', 'Back')} />
        <button type="button" className="support-widget-sheet__close" data-support-close
                aria-label={t('T.SUPPORT-WIDGET.CLOSE', 'Close')} />

        <div className="support-widget-sheet__view support-widget-sheet__view--channels">
          <div className="support-widget-sheet__header">
            <span className="support-widget-sheet__avatar">
              <span className="support-widget__avatar-photo">
                <img src={agentImage} width="72" height="72" alt="" decoding="async" />
              </span>
              <span className="support-widget__dot" aria-hidden="true" />
            </span>
            <h2 className="support-widget-sheet__title">
              {t('T.SUPPORT-WIDGET.SHEET.TITLE', 'Need help?')}
            </h2>
            <p className="support-widget-sheet__subtitle">
              {t('T.SUPPORT-WIDGET.SHEET.SUBTITLE', 'Our team is here for whatever you need.')}
            </p>
          </div>

          <div className="support-widget-sheet__channels">
            { chatEnabled &&
              <button type="button" className="support-widget-sheet__channel"
                      data-support-channel="chat" aria-busy={chatBusy}>
                <span className="support-widget-sheet__channel-label">
                  {t('T.SUPPORT-WIDGET.CHANNEL.CHAT', 'Chat')}
                </span>
                <span className="support-widget-sheet__channel-icon" aria-hidden="true" />
              </button>
            }

            { callEnabled &&
              <a className="support-widget-sheet__channel" data-support-channel="phone" href={`tel:${phone}`}>
                <span className="support-widget-sheet__channel-label">
                  {t('T.SUPPORT-WIDGET.CHANNEL.PHONE', 'Phone')}
                </span>
                <span className="support-widget-sheet__channel-icon" aria-hidden="true" />
              </a>
            }
          </div>
        </div>

        { /* Desktop only: the core swaps to this view instead of following the
             tel: link. On mobile the link is never intercepted — it is the button
             mobile customers actually press. */ }
        { callEnabled &&
          <div className="support-widget-sheet__view support-widget-sheet__view--call" data-support-call-list>
            <div className="support-widget-sheet__header">
              <span className="support-widget-sheet__avatar">
                <span className="support-widget__avatar-photo">
                  <img src={agentImage} width="72" height="72" alt="" decoding="async" />
                </span>
                <span className="support-widget__dot" aria-hidden="true" />
              </span>
              <h2 className="support-widget-sheet__title">
                {t('T.SUPPORT-WIDGET.SHEET.CALL-TITLE', 'Customer line')}
              </h2>
              <a className="support-widget-sheet__number" href={`tel:${phone}`}>{phone}</a>
            </div>
            { Boolean(hoursLabel) &&
              <p className="support-widget-sheet__hours">{hoursLabel}</p>
            }
          </div>
        }
      </div>
    </aside>
  );
};

export default SupportSheet;
